import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api } from "../api";
import { ConfirmDialog } from "../ConfirmDialog";

const STATUS = {
  pending: { label: "Bekliyor", cls: "bg-cream-300 text-ink-800" },
  confirmed: { label: "Onaylı", cls: "bg-ink-900 text-cream-50" },
  cancelled: { label: "İptal", cls: "bg-white text-ink-400 line-through" },
};

const RandevularTab = () => {
  const [items, setItems] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = () => api.get("/admin/appointments").then(({ data }) => setItems(data.appointments)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/appointments/${id}`, { status });
      toast.success("Randevu güncellendi");
      load();
    } catch {
      toast.error("Güncelleme başarısız");
    }
  };

  const remove = (id) => {
    setConfirm({
      message: "Bu randevu kalıcı olarak silinsin mi?",
      confirmLabel: "Evet, Sil",
      action: async () => {
        await api.delete(`/admin/appointments/${id}`);
        toast.success("Randevu silindi");
        load();
      },
    });
  };

  if (!items) return <p className="text-sm text-ink-500">Yükleniyor...</p>;

  const groups = items.reduce((acc, a) => {
    (acc[a.date] = acc[a.date] || []).push(a);
    return acc;
  }, {});
  const dates = Object.keys(groups).sort();

  return (
    <div data-testid="randevular-tab">
      <h1 className="text-2xl font-bold tracking-tight text-ink-900 sm:text-3xl">Randevular</h1>
      <p className="mt-2 text-sm text-ink-500">Yerinde görüşme talepleri, tarihe göre takvim düzeninde.</p>

      {dates.length === 0 && (
        <div className="mt-8 border border-line bg-white p-10 text-center text-sm text-ink-400" data-testid="randevu-empty">
          Henüz randevu talebi yok.
        </div>
      )}

      <div className="mt-8 space-y-8">
        {dates.map((date) => (
          <div key={date}>
            <p className="label text-ink-500" data-testid={`randevu-date-${date}`}>
              {new Date(date + "T00:00:00").toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </p>
            <div className="mt-3 space-y-px border border-line bg-line">
              {groups[date].map((a) => (
                <div key={a.id} className="card-lift flex flex-col gap-4 bg-white p-5 sm:flex-row sm:items-center sm:justify-between" data-testid={`randevu-item-${a.id}`}>
                  <div>
                    <p className="text-base font-bold tracking-tight text-ink-900">{a.name}</p>
                    <p className="mt-1 text-sm text-ink-500">{a.phone} · {a.time_slot}</p>
                    {a.note && <p className="mt-1 text-xs text-ink-400">{a.note}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] ${STATUS[a.status].cls}`}>
                      {STATUS[a.status].label}
                    </span>
                    {a.status !== "confirmed" && (
                      <button onClick={() => setStatus(a.id, "confirmed")} data-testid={`randevu-confirm-${a.id}`}
                        className="icon-btn border border-ink-900 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-ink-900 hover:bg-ink-900 hover:text-cream-50">
                        Onayla
                      </button>
                    )}
                    {a.status !== "cancelled" && (
                      <button onClick={() => setStatus(a.id, "cancelled")} data-testid={`randevu-cancel-${a.id}`}
                        className="icon-btn border border-line px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-ink-400 hover:border-ink-900 hover:text-ink-900">
                        İptal
                      </button>
                    )}
                    <button onClick={() => remove(a.id)} data-testid={`randevu-delete-${a.id}`} aria-label="Sil"
                      className="icon-btn border border-line p-1.5 text-ink-400 hover:border-red-600 hover:text-red-600">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <ConfirmDialog
        open={!!confirm}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        onCancel={() => setConfirm(null)}
        onConfirm={async () => {
          const action = confirm?.action;
          setConfirm(null);
          if (action) await action();
        }}
      />
    </div>
  );
};

export default RandevularTab;
